import { ConsultPatientDto, PatientDto } from 'app/models/generated'
import { logger } from 'app/logging/Logger'
import {
    executeQuery,
    executeMutation,
    fetchVersion,
} from 'app/api/client/graphqlClient'
import {
    consultPatientDtosByConsultId,
    createConsultPatientDtoMutation,
    updateConsultPatientDtoMutation,
} from 'app/graphql/customQueries'
import { isNotDeleted } from './softDelete'

export interface ConsultPatientRepository {
    getByConsultId(consultId: string): Promise<ConsultPatientDto | null>
    savePatient(
        consultId: string,
        patientId?: string | null,
        attachedPatient?: PatientDto | null,
        matchedPatients?: PatientDto[] | null
    ): Promise<void>
}

interface ConsultPatientDtoResult {
    id: string
    consultId: string
    owner?: string
    patientId?: string | null
    attachedPatient?: PatientDto | null
    matchedPatients?: PatientDto[] | null
}

interface ConsultPatientDtosByConsultIdResult {
    items: ConsultPatientDtoResult[]
    nextToken: string | null
}

/**
 * GraphQL API implementation of ConsultPatientRepository.
 * Manages patient data via AppSync GraphQL operations.
 */
export class DataStoreConsultPatientRepository
    implements ConsultPatientRepository
{
    async getByConsultId(consultId: string): Promise<ConsultPatientDto | null> {
        try {
            logger.debug('Querying patient data by consultId', {
                action: 'get_patient_data',
                metadata: { consultId },
            })

            const result =
                await executeQuery<ConsultPatientDtosByConsultIdResult>(
                    consultPatientDtosByConsultId,
                    { consultId },
                    'consultPatientDtosByConsultId'
                )

            const items = result.items.filter(isNotDeleted)

            if (items.length === 0) {
                logger.debug('No patient data found for consult', {
                    action: 'get_patient_data_not_found',
                    metadata: { consultId },
                })
                return null
            }

            if (items.length > 1) {
                logger.warn('Multiple patient records found for consult', {
                    action: 'get_patient_data_multiple',
                    metadata: { consultId, count: items.length },
                })
            }

            const record = items[0]
            logger.debug('Patient data found', {
                action: 'get_patient_data_success',
                metadata: {
                    consultId,
                    hasPatientId: !!record.patientId,
                    hasAttachedPatient: !!record.attachedPatient,
                    matchedPatientsCount: record.matchedPatients?.length || 0,
                },
            })
            return record as ConsultPatientDto
        } catch (error) {
            logger.error('Failed to fetch patient data', error as Error, {
                action: 'get_patient_data_error',
                metadata: { consultId },
            })
            throw error
        }
    }

    async savePatient(
        consultId: string,
        patientId?: string | null,
        attachedPatient?: PatientDto | null,
        matchedPatients?: PatientDto[] | null
    ): Promise<void> {
        try {
            // Query for existing patient record by consultId
            const result =
                await executeQuery<ConsultPatientDtosByConsultIdResult>(
                    consultPatientDtosByConsultId,
                    { consultId },
                    'consultPatientDtosByConsultId'
                )

            const existing = result.items.filter(isNotDeleted)

            if (existing.length > 0) {
                // Update existing record
                const record = existing[0]
                const _version = await fetchVersion(
                    'ConsultPatientDto',
                    record.id
                )

                await executeMutation(
                    updateConsultPatientDtoMutation,
                    {
                        input: {
                            id: record.id,
                            patientId,
                            attachedPatient,
                            matchedPatients,
                            _version,
                        },
                    },
                    'updateConsultPatientDto'
                )

                logger.info('Patient data updated successfully', {
                    action: 'update_patient',
                    metadata: { consultId, hasPatientId: !!patientId },
                })
            } else {
                // Create new record
                await executeMutation(
                    createConsultPatientDtoMutation,
                    {
                        input: {
                            consultId,
                            patientId,
                            attachedPatient,
                            matchedPatients,
                        },
                    },
                    'createConsultPatientDto'
                )

                logger.info('Patient data created successfully', {
                    action: 'create_patient',
                    metadata: { consultId, hasPatientId: !!patientId },
                })
            }
        } catch (error) {
            logger.error('Failed to save patient data', error as Error, {
                action: 'save_patient_error',
                metadata: { consultId },
            })
            throw error
        }
    }
}

export const consultPatientRepository = new DataStoreConsultPatientRepository()
